import { FC, useContext, useEffect, useRef, useState } from "react";

import { FormCategory, FormType, parseFormBackgroundTheme } from "@simplyk/common";

import { FloatingChooseTicketsButton } from "../../../blocks/selection/ticketing-selection/FloatingChooseTicketsButton/FloatingChooseTicketsButton";
import { OccurrenceSelectorProvider } from "../../../blocks/selection/ticketing-selection/OccurrenceSelector/OccurrenceSelectorContext";
import { Rates } from "../../../blocks/selection/ticketing-selection/Rates/Rates";
import { TicketingV2AdditionalDonation } from "../../../blocks/selection/ticketing-selection/TicketingV2AdditionalDonation";
import { BurstProvider } from "../../../contexts/burstContext";
import { useFormV2Context } from "../../../contexts/FormV2Context";
import { HeartsAnimationProvider } from "../../../contexts/heartsAnimationContext";
import { AuctionCheckoutFooter } from "../shared/CheckoutFooter/AuctionCheckoutFooter";

import { AuctionWelcomeBackBanner } from "./AuctionWelcomeBackBanner";

import { formBackgroundThemeToBurstType } from "@/canvas/components/Burst/formBackgroundThemeToBurstType";
import { MobileCampaignBanner } from "@/canvas/features/FormV2/blocks/campaign-details/CampaignBanner/MobileCampaignBanner";
import { GenerateReceiptInfobox } from "@/canvas/features/FormV2/blocks/payment/GenerateReceiptInfobox";
import { AuctionSummary } from "@/canvas/features/FormV2/blocks/payment/Summary/AuctionSummary";
import { ZeffyBusinessModelInfobox } from "@/canvas/features/FormV2/blocks/payment/ZeffyBusinessModelInfobox";
import { useEnterKeyListener } from "@/canvas/features/FormV2/hooks/useEnterKeyListener";
import { useTrackFormLogoClicked } from "@/canvas/features/FormV2/hooks/useTrackFormLogoClicked";
import { useUtmForm } from "@/canvas/features/FormV2/hooks/useUtmForm";
import { AuctionStep1Layout } from "@/canvas/features/FormV2/steps/step1/Auction/AuctionStep1Layout";
import { useAuctionFirstStepSubmit } from "@/canvas/features/FormV2/steps/step1/shared/CheckoutFooter/useAuctionFirstStepSubmit";
import { AuctionV2CoreInfo } from "@/canvas/features/FormV2/steps/step1/shared/CoreInfo/AuctionV2CoreInfo";
import { useFrontendAuctionContext } from "@/common/contexts/FrontendAuctionContext";
import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { useThemedFormZeffyLogoUrl } from "@/common/hooks/useThemedFormZeffyLogoUrl";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { NotificationBannerHeightContext } from "@/components/NotificationBanners/NotificationBannerHeightContext";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { usePreviewContext } from "@/features/LiveFormEditor/LivePreview/contexts/PreviewContext";
import { FormDivider } from "@/forms-design-system/FormDivider";

const SCROLL_THRESHOLD = 100;

export const AuctionStep1: FC = () => {
  const { formObject, hasBanner, currentStep, description, bannerAspectRatio, isEmbed } = useFormV2Context(
    FormType.Ticketing
  );
  const { isTicketingClosed: ticketingIsClosed } = useFormV2Context(FormType.Ticketing);
  const { isReturningBidder } = useFrontendAuctionContext();
  const { onFirstStepSubmit, canSubmitFirstStep } = useAuctionFirstStepSubmit();
  const { isPreview } = usePreviewContext();
  const { isExpanded, bannerHeight } = useContext(NotificationBannerHeightContext);
  const utm = useUtmForm();

  const burstType = formBackgroundThemeToBurstType(parseFormBackgroundTheme(formObject.backgroundTheme));

  const ratesRef = useRef<HTMLDivElement>(null);

  const onTrackLogoClick = useTrackFormLogoClicked({
    formType: FormType.Ticketing,
    formCategory: FormCategory.Auction,
    country: formObject.organization?.country,
    formStep: 1,
  });

  const logoHref = useThemedFormZeffyLogoUrl(utm);

  const isCampaign = !!formObject.thermometerTarget;
  const isOneColumnLayoutV2 = !hasBanner && !description && !isCampaign;
  const isSquareBanner = bannerAspectRatio === "1/1";

  const showCheckoutFooter = canSubmitFirstStep && !isPreview;

  const [hasScrolledPastThreshold, setHasScrolledPastThreshold] = useState(false);
  const { isSmallScreen, isPhoneScreen } = useMediaQuery();

  useEffect(() => {
    if (!isSquareBanner) {
      return;
    }

    const scrollContainer =
      document.querySelector<HTMLElement>('[data-test="form-scrollable-area"]') ?? document.documentElement;

    const handleScroll = () => {
      if (scrollContainer.scrollTop > SCROLL_THRESHOLD) {
        setHasScrolledPastThreshold(true);
      }
    };

    scrollContainer.addEventListener("scroll", handleScroll, { passive: true });

    handleScroll();

    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
    };
  }, [isSquareBanner]);

  const shouldShowButton = isSquareBanner && isSmallScreen && !hasScrolledPastThreshold;

  useEnterKeyListener(onFirstStepSubmit, currentStep === "selection");

  const additionalDonationPosition: "top-of-page" | "default" = ticketingIsClosed ? "top-of-page" : "default";

  const handleScrollToTickets = () => {
    if (ratesRef.current) {
      ratesRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <OccurrenceSelectorProvider>
      <HeartsAnimationProvider>
        <BurstProvider
          type={burstType}
          brandColorHex={formObject.color}
          canBurstHeart={Boolean(formObject.extraDonation)}
        >
          <AuctionStep1Layout
            contextDrawerHeight={isExpanded ? bannerHeight : 0}
            additionalDonationPosition={additionalDonationPosition}
            showMobileChooseTicketsFloatingButton={shouldShowButton}
            isEmbed={isEmbed}
            isOneColumn={isOneColumnLayoutV2}
            showCheckoutFooter={showCheckoutFooter}
            logoHref={logoHref}
            onLogoClick={onTrackLogoClick}
            isSquareBanner={isSquareBanner}
            slots={{
              banner: hasBanner ? <MobileCampaignBanner useSquareRatioOnMobile={Boolean(isSquareBanner)} /> : undefined,
              welcomeBackBanner: isReturningBidder && !isPhoneScreen ? <AuctionWelcomeBackBanner /> : undefined,
              summary: <AuctionSummary />,
              coreInfo: <AuctionV2CoreInfo />,
              additionalDonation: <TicketingV2AdditionalDonation />,
              rates: (
                <div ref={ratesRef}>
                  <Rates />
                </div>
              ),
              generateReceipt: <GenerateReceiptInfobox />,
              zeffyBusinessModel: <ZeffyBusinessModelInfobox />,
              checkoutFooter: <AuctionCheckoutFooter />,
              mobileChooseTicketsFloatingButton: (
                <FloatingChooseTicketsButton show={shouldShowButton} onClickScrollToTickets={handleScrollToTickets} />
              ),
              divider: !isOneColumnLayoutV2 ? <FormDivider sx={{ width: "100%" }} /> : undefined,
            }}
          />
        </BurstProvider>
      </HeartsAnimationProvider>
    </OccurrenceSelectorProvider>
  );
};
